import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaClock, FaUserTie, FaChalkboardTeacher } from 'react-icons/fa';

function CourseDetail() {
    const { courseId } = useParams();
    const navigate = useNavigate();

    const courses = {
        'stock-market': {
            title: "Stock Market Beginner to Advance",
            duration: "60 Days",
            color: "from-cyan-500 to-blue-600",
            details: {
                overview: "This comprehensive course takes you from the basics of stock market investing to advanced trading strategies. You'll learn how to analyze companies, read financial statements, and develop your own investment strategy.",
                syllabus: [
                    "Introduction to Stock Markets",
                    "Fundamental Analysis Techniques",
                    "Technical Analysis & Chart Patterns",
                    "Options Trading Strategies",
                    "Portfolio Management",
                    "Risk Management Techniques",
                    "Algorithmic Trading Basics"
                ],
                benefits: [
                    "Understand market trends and indicators",
                    "Develop profitable trading strategies",
                    "Manage investment risks effectively",
                    "Build a diversified portfolio",
                    "Access to real-time trading simulations"
                ],
                mentor: "Abhishek Sharma",
                // price: "₹15,999",
                // discount: "₹14,999",
                mentorBio: "12+ years experience in equity, Options and derivatives markets."
            }
        },
        'spoken-english': {
            title: "Spoken English",
            duration: "30 Days",
            color: "from-amber-500 to-orange-600",
            details: {
                overview: "Transform your English communication skills in just 30 days with our intensive spoken English program. Designed for professionals and students who want to speak confidently in any situation.",
                syllabus: [
                    "Pronunciation & Accent Training",
                    "Business Communication Skills",
                    "Public Speaking Techniques",
                    "Grammar Refresher",
                    "Listening Comprehension",
                    "Interview Preparation",
                    "Group Discussion Practice"
                ],
                benefits: [
                    "Speak fluently without hesitation",
                    "Improve pronunciation and accent",
                    "Gain confidence in professional settings",
                    "Enhance interview performance",
                    "Develop active listening skills"
                ],
                mentor: "Megha Sharma",
                price: "₹7,999",
                discount: "₹4,999",
                mentorBio: "Certified English trainer with 8 years experience."
            }
        },
        'digital-marketing': {
            title: "Digital Marketing Course",
            duration: "45 Days",
            color: "from-fuchsia-500 to-purple-600",
            details: {
                overview: "Become a digital marketing expert with our hands-on training program. Learn the latest tools and techniques to create, manage, and optimize digital marketing campaigns across all platforms.",
                syllabus: [
                    "Search Engine Optimization (SEO)",
                    "Social Media Marketing Strategies",
                    "Content Marketing & Copywriting",
                    "Google Ads & PPC Campaigns",
                    "Email Marketing Automation",
                    "Analytics & Data Interpretation",
                    "Conversion Rate Optimization"
                ],
                benefits: [
                    "Master all digital marketing channels",
                    "Create effective marketing campaigns",
                    "Analyze and optimize performance",
                    "Build a professional portfolio",
                    "Get certified as a digital marketing expert"
                ],
                mentor: "Abhishek Sharma",
                price: "₹19,999",
                discount: "₹14,999",
                mentorBio: "10 years experience scaling brands online."
            }
        }
    };

    const course = courses[courseId];

    if (!course) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Course not found</div>;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center text-cyan-400 hover:text-cyan-300 mb-8 transition-colors"
                >
                    <FaArrowLeft className="mr-2" /> Back to Courses
                </button>

                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl overflow-hidden shadow-2xl">
                    {/* Course Header */}
                    <div className={`relative ${course.color.replace('from-', 'bg-gradient-to-r from-')} p-8 sm:p-12`}>
                        <div className="absolute inset-0 bg-black opacity-20"></div>
                        <div className="relative z-10">
                            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{course.title}</h1>
                            <div className="flex items-center space-x-4">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-black/20 text-white">
                                    <FaClock className="mr-1" /> {course.duration}
                                </span>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-black/20 text-white">
                                    <FaUserTie className="mr-1" /> {course.details.mentor}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6 sm:p-8">
                        {/* Main Content */}
                        <div className="lg:col-span-2">
                            {/* Overview */}
                            <div className="mb-10">
                                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                    <span className={`w-6 h-6 rounded-full ${course.color.replace('from-', 'bg-gradient-to-r from-')} mr-3 flex items-center justify-center`}>
                                        <span className="text-xs">1</span>
                                    </span>
                                    Course Overview
                                </h2>
                                <p className="text-gray-300">{course.details.overview}</p>
                            </div>

                            {/* Syllabus */}
                            <div className="mb-10">
                                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                    <span className={`w-6 h-6 rounded-full ${course.color.replace('from-', 'bg-gradient-to-r from-')} mr-3 flex items-center justify-center`}>
                                        <span className="text-xs">2</span>
                                    </span>
                                    What You'll Learn
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {course.details.syllabus.map((item, index) => (
                                        <div key={index} className="flex items-start">
                                            <div className={`w-5 h-5 rounded-full ${course.color.replace('from-', 'bg-gradient-to-r from-')} flex-shrink-0 mt-1 flex items-center justify-center`}>
                                                <FaCheck className="text-white text-xs" />
                                            </div>
                                            <p className="ml-3 text-gray-300">{item}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Benefits */}
                            <div className="mb-10">
                                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                                    <span className={`w-6 h-6 rounded-full ${course.color.replace('from-', 'bg-gradient-to-r from-')} mr-3 flex items-center justify-center`}>
                                        <span className="text-xs">3</span>
                                    </span>
                                    Key Benefits
                                </h2>
                                <ul className="space-y-3">
                                    {course.details.benefits.map((benefit, index) => (
                                        <li key={index} className="flex items-start">
                                            <div className={`w-5 h-5 rounded-full ${course.color.replace('from-', 'bg-gradient-to-r from-')} flex-shrink-0 mt-1 flex items-center justify-center`}>
                                                <FaCheck className="text-white text-xs" />
                                            </div>
                                            <p className="ml-3 text-gray-300">{benefit}</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-1">
                            <div className="bg-gray-800/50 rounded-xl p-6 sticky top-6">
                                <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                                    <FaChalkboardTeacher className={`mr-2 ${course.color.replace('from-', 'text-').split(' ')[0]}`} />
                                    Course Details
                                </h3>

                                <div className="space-y-6">
                                    {/* Pricing */}
                                    <div>
                                        {/* <h4 className="text-sm font-medium text-gray-400 mb-2">Pricing</h4> */}
                                        <div className="flex items-baseline">
                                            <span className="text-3xl font-bold text-white">{course.details.discount}</span>
                                            <span className="ml-2 text-lg text-gray-400 line-through">{course.details.price}</span>
                                        </div>
                                    </div>

                                    {/* Mentor */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-400 mb-2">Mentor</h4>
                                        <p className="text-white font-medium">{course.details.mentor}</p>
                                        <p className="text-gray-400 text-sm mt-1">{course.details.mentorBio}</p>
                                    </div>

                                    {/* Duration */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-400 mb-2">Duration</h4>
                                        <p className="text-white">{course.duration} Program</p>
                                        <p className="text-gray-400 text-sm mt-1">Daily 2-hour sessions</p>
                                    </div>

                                    {/* Enroll Button */}
                                    <button 
                                        className={`w-full py-3 px-6 rounded-xl font-bold text-white bg-gradient-to-r ${course.color} hover:opacity-90 transition-all shadow-lg hover:shadow-xl ${course.color.replace('from-', 'shadow-').replace('to-', '/30')}`}
                                    >
                                        Enroll Now
                                    </button>

                                    {/* Additional Info */}
                                    {/* <div className="text-center text-sm text-gray-400">
                                        <p>30-day money back guarantee</p>
                                        <p className="mt-1">Certificate upon completion</p>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CourseDetail;